import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, ArrowUpRight, Loader2, Bookmark } from "lucide-react";
import { Footer } from "@/components/site/Footer";
import { ShareFeature } from "@/components/site/ShareFeature";
import { markViewed, isSaved, toggleSave, useLibraryVersion } from "@/lib/library";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function mediaUrl(src) {
  if (!src) return "";
  if (src.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${src}`;
  return src;
}

function parseBlock(item) {
  if (typeof item === "string" && item.startsWith("::img::")) {
    const rest = item.slice(7);
    const [url, caption = ""] = rest.split("::cap::");
    return { type: "img", url: url.trim(), caption: caption.trim() };
  }
  return { type: "p", text: item };
}

function youtubeId(url) {
  if (!url) return "";
  const m = url.match(/(?:youtu\.be\/|v=|\/embed\/|\/shorts\/)([\w-]{11})/);
  return m ? m[1] : "";
}

const TopBar = () => (
  <header className="fixed top-0 left-0 right-0 z-[1000] bg-black/70 backdrop-blur-xl border-b border-white/10">
    <div className="flex items-center justify-between px-5 md:px-10 h-[68px]">
      <Link to="/preview" data-testid="detail-logo" className="font-display text-2xl md:text-3xl tracking-tight leading-none">
        <span className="text-[#E30613]">VL</span>
        <span className="text-white">MAG</span>
      </Link>
      <Link
        to="/preview#stories"
        data-testid="detail-back"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-neutral-400 hover:text-white transition-colors"
      >
        <ArrowLeft size={16} /> All Stories
      </Link>
    </div>
  </header>
);

export default function StoryDetail() {
  const { id } = useParams();
  const [story, setStory] = useState(null);
  const [related, setRelated] = useState([]);
  const [seriesParts, setSeriesParts] = useState([]);
  const [status, setStatus] = useState("loading");
  useLibraryVersion();

  const shareUrl = `${process.env.REACT_APP_BACKEND_URL}/api/share/${id}`;

  useEffect(() => {
    if (!story) return;
    const prevTitle = document.title;
    const metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc?.getAttribute("content");
    document.title = `${story.title} — ${story.network || "VLMAG"}`;
    metaDesc?.setAttribute("content", story.excerpt || prevDesc || "");
    return () => {
      document.title = prevTitle;
      if (prevDesc != null) metaDesc?.setAttribute("content", prevDesc);
    };
  }, [story]);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    let active = true;
    setStatus("loading");
    axios
      .get(`${API}/stories/${id}`)
      .then((r) => {
        if (!active) return;
        setStory(r.data);
        setStatus("ready");
        markViewed(r.data.id);
        const series = r.data.series;
        axios
          .get(`${API}/stories`)
          .then((all) => {
            if (!active) return;
            const list = all.data;
            setRelated(list.filter((s) => s.id !== id).slice(0, 3));
            if (series) {
              const parts = list
                .filter((s) => s.series === series)
                .sort((a, b) => (a.part || 0) - (b.part || 0));
              setSeriesParts(parts);
            }
          })
          .catch(() => {});
      })
      .catch(() => active && setStatus("error"));
    return () => { active = false; };
  }, [id]);

  if (status === "loading") {
    return (
      <div className="bg-black text-white min-h-screen flex items-center justify-center" data-testid="story-loading">
        <Loader2 className="animate-spin text-[#E30613]" size={32} />
      </div>
    );
  }

  if (status === "error" || !story) {
    return (
      <div className="bg-black text-white min-h-screen flex flex-col items-center justify-center gap-6 px-6 text-center" data-testid="story-not-found">
        <h1 className="font-display uppercase text-5xl md:text-7xl tracking-tight">Story Not Found</h1>
        <Link to="/preview#stories" className="border border-[#E30613] px-6 py-3 text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#E30613] transition-colors">
          Back to Stories
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-black text-white min-h-screen" data-testid="story-detail">
      <TopBar />

      {/* Hero */}
      <section className="relative pt-[68px]">
        <div className="relative h-[62vh] md:h-[78vh] overflow-hidden">
          <img src={mediaUrl(story.image)} alt={story.title} className="w-full h-full object-cover grayscale-img" data-testid="detail-image" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 px-5 md:px-10 pb-10 md:pb-16">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="max-w-5xl"
            >
              <div className="flex items-center gap-3 mb-5 flex-wrap">
                <span className="bg-[#E30613] px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold" data-testid="detail-network">{story.network}</span>
                {story.series && (
                  <span className="inline-flex items-center gap-2 border border-[#E30613] text-[#E30613] px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold" data-testid="detail-series">
                    {story.series}{story.part ? ` · Part ${story.part}` : ""}
                  </span>
                )}
                <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-400">{story.category}</span>
              </div>
              <h1 className="font-display uppercase leading-[0.9] tracking-tight text-4xl sm:text-5xl md:text-7xl" data-testid="detail-title">
                {story.title}
              </h1>
              <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-neutral-400 mt-6" data-testid="detail-author">{story.author}</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Body */}
      <article className="px-5 md:px-10 py-16 md:py-24">
        <div className="max-w-3xl mx-auto" data-testid="detail-content">
          <p className="font-serif-ed italic text-2xl md:text-3xl text-white leading-snug mb-12 border-l-2 border-[#E30613] pl-6">
            {story.excerpt}
          </p>

          <div className="mb-12 flex flex-wrap items-center gap-3">
            <button
              onClick={() => toggleSave(story)}
              data-testid="detail-save"
              className={`inline-flex items-center gap-2 px-5 h-11 border text-[10px] uppercase tracking-[0.2em] font-bold transition-colors ${isSaved(story.id) ? "bg-[#E30613] border-[#E30613] text-white" : "border-white/25 text-neutral-300 hover:text-white hover:border-white/60"}`}
            >
              <Bookmark size={14} fill={isSaved(story.id) ? "#fff" : "none"} />
              {isSaved(story.id) ? "Saved — Read Offline" : "Save For Later"}
            </button>
            <div className="flex-1 min-w-[200px]">
              <ShareFeature shareUrl={shareUrl} title={story.title} cardUrl={story.og_image} storyId={story.id} variant="compact" />
            </div>
          </div>

          {story.product_url && (
            <a
              href={story.product_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="detail-shop-drop"
              className="group flex items-center justify-between gap-4 border border-[#E30613] bg-[#E30613]/10 hover:bg-[#E30613]/20 transition-colors duration-300 px-5 md:px-6 py-5 mb-12"
            >
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#E30613] mb-1">{story.product_label || "Shop The Drop"}</p>
                <p className="text-white font-bold text-base md:text-lg leading-snug">Get the piece from this feature at {story.product_url.replace(/^https?:\/\/(www\.)?/, "").replace(/\/.*$/, "")}</p>
              </div>
              <span className="shrink-0 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-white">
                Shop Now
                <ArrowUpRight size={18} className="text-[#E30613] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
              </span>
            </a>
          )}

          {youtubeId(story.video_url) && (
            <div className="relative w-full aspect-video mb-12 border border-white/10" data-testid="detail-video">
              <iframe
                className="absolute inset-0 w-full h-full"
                src={`https://www.youtube.com/embed/${youtubeId(story.video_url)}`}
                title={story.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}
          {(() => {
            let pIndex = -1;
            return (story.content || []).map((item, i) => {
              const block = parseBlock(item);
              if (block.type === "img") {
                return (
                  <figure key={i} className="my-12 -mx-5 md:mx-0" data-testid={`detail-body-image-${i}`}>
                    <div className="overflow-hidden border border-white/10">
                      <img src={mediaUrl(block.url)} alt={block.caption || story.title} className="w-full h-auto object-cover" loading="lazy" />
                    </div>
                    {block.caption && (
                      <figcaption className="mt-3 px-5 md:px-0 text-[11px] uppercase tracking-[0.2em] font-bold text-neutral-500">
                        {block.caption}
                      </figcaption>
                    )}
                  </figure>
                );
              }
              pIndex += 1;
              const isFirst = pIndex === 0;
              return (
                <p
                  key={i}
                  className={`text-neutral-300 leading-relaxed mb-6 ${isFirst ? "text-lg md:text-xl first-letter:font-display first-letter:text-6xl first-letter:float-left first-letter:mr-3 first-letter:leading-[0.8] first-letter:text-[#E30613]" : "text-base md:text-lg"}`}
                >
                  {block.text}
                </p>
              );
            });
          })()}

          <div className="mt-14">
            <ShareFeature shareUrl={shareUrl} title={story.title} cardUrl={story.og_image} storyId={story.id} variant="full" />
          </div>

          <div className="mt-14 pt-10 border-t border-white/10">
            <Link
              to="/preview#featured"
              data-testid="detail-cta"
              className="group inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] font-bold text-white"
            >
              Get Featured on VLMAG
              <ArrowUpRight size={18} className="text-[#E30613] group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </Link>
          </div>
        </div>
      </article>

      {/* Series */}
      {story.series && seriesParts.length > 1 && (
        <section className="px-5 md:px-10 pb-4 pt-16 md:pt-24 border-t border-white/10" data-testid="detail-series-module">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#E30613] mb-8">The Series — {story.series}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {seriesParts.map((s) => {
              const isCurrent = s.id === story.id;
              return (
                <Link
                  key={s.id}
                  to={`/story/${s.id}`}
                  data-testid={`series-part-${s.part || 0}`}
                  className={`group border transition-colors duration-500 block ${isCurrent ? "border-[#E30613]" : "border-white/10 hover:border-[#E30613]"}`}
                >
                  <div className="relative aspect-[16/9] overflow-hidden">
                    <img src={mediaUrl(s.image)} alt={s.title} className="w-full h-full object-cover grayscale-img group-hover:scale-105 transition-transform duration-700" />
                    <span className="absolute top-0 left-0 bg-[#E30613] px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-white">
                      Part {s.part || "—"}
                    </span>
                    {isCurrent && (
                      <span className="absolute top-0 right-0 bg-black px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-300 border-l border-b border-white/10">
                        Now Reading
                      </span>
                    )}
                  </div>
                  <div className="p-5">
                    <h3 className="font-display uppercase text-lg md:text-xl leading-[0.95] tracking-tight text-white group-hover:text-[#E30613] transition-colors duration-300">
                      {s.title}
                    </h3>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* Related */}
      {related.length > 0 && (
        <section className="px-5 md:px-10 pb-20 md:pb-28 border-t border-white/10 pt-16 md:pt-24">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#E30613] mb-8">Keep Reading</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8" data-testid="related-stories">
            {related.map((s) => (
              <Link
                key={s.id}
                to={`/story/${s.id}`}
                data-testid={`related-${s.id}`}
                className="group border border-white/10 hover:border-[#E30613] transition-colors duration-500 block"
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img src={mediaUrl(s.image)} alt={s.title} className="w-full h-full object-cover grayscale-img group-hover:scale-105 transition-transform duration-700" />
                  <span className="absolute top-0 left-0 bg-black px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-[#E30613] border-r border-b border-white/10">
                    {s.network}
                  </span>
                </div>
                <div className="p-5 md:p-6">
                  <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">{s.category}</span>
                  <h3 className="font-display uppercase text-xl md:text-2xl leading-[0.95] tracking-tight text-white group-hover:text-[#E30613] transition-colors duration-300 mt-3">
                    {s.title}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
